import { useState } from "react";
import {
  createFileRoute,
  redirect,
  useSearch,
  Link,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/auth/google-signin-button";
import { signIn } from "@/lib/auth-client";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";

const sendVerificationEmail = async (email: string) => {
  const response = await fetch("/api/auth/send-verification-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error("Failed to send verification email");
  }
};

export const Route = createFileRoute("/login")({
  beforeLoad: async ({ context }) => {
    const session = context.session;
    if (session) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: LoginPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string; redirect?: string; emailNotVerified?: string } => ({
    error: search.error as string | undefined,
    redirect: search.redirect as string | undefined,
    emailNotVerified: search.emailNotVerified as string | undefined,
  }),
});

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Sign-in was cancelled. Please try again.",
  server_error: "Unable to sign in. Please try again later.",
  invalid_request: "Configuration error. Please contact support.",
  INVALID_EMAIL_OR_PASSWORD: "Invalid email or password. Please try again.",
  EMAIL_NOT_VERIFIED:
    "Your email is not verified. Please check your inbox or request a new verification link.",
};

function LoginPage() {
  const search = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = search.redirect || "/dashboard";

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const { error: signInError } = await signIn.email({
        email,
        password,
        callbackURL: redirectTo,
      });
      if (signInError) {
        if (signInError.code === "EMAIL_NOT_VERIFIED") {
          setError("EMAIL_NOT_VERIFIED");
        } else {
          setError(signInError.code || "Invalid email or password");
        }
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendVerificationEmail(email);
      window.location.href = `/verify-pending?email=${encodeURIComponent(email)}`;
    } catch {
      setError("Failed to send verification email. Please try again.");
    }
  };

  const displayError = search.error
    ? ERROR_MESSAGES[search.error] || "An error occurred during sign in."
    : error
      ? ERROR_MESSAGES[error] || "An error occurred."
      : null;

  const showResendButton = error === "EMAIL_NOT_VERIFIED";

  return (
    <div className="flex min-h-screen bg-background cinematic-noise">
      {/* Left Pane - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-16 xl:px-24 py-16 sm:py-12 relative z-10">
        <div
          className="max-w-md w-full mx-auto space-y-8 sm:space-y-10 animate-fade-in"
          style={{ animationDelay: "100ms", animationFillMode: "both" }}
        >
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-4xl sm:text-5xl font-serif tracking-tight">
              Welcome back.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground font-light">
              Sign in to continue your creative journey.
            </p>
          </div>

          {displayError && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 animate-fade-in">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-destructive font-medium">
                  {displayError}
                </p>
                {showResendButton && (
                  <Button
                    variant="link"
                    className="h-auto p-0 text-destructive underline self-start"
                    onClick={handleResendVerification}
                  >
                    Resend verification email
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="space-y-6">
            <GoogleSignInButton callbackURL={redirectTo} />

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-border"></div>
              <span className="flex-shrink-0 mx-4 text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">
                Or continue with
              </span>
              <div className="flex-grow border-t border-border"></div>
            </div>

            <form onSubmit={handleEmailSignIn} className="space-y-5">
              <div className="space-y-2 group">
                <Label
                  htmlFor="email"
                  className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground group-focus-within:text-foreground transition-colors"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="h-10 sm:h-12 bg-transparent border-x-0 border-t-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-sm sm:text-base"
                />
              </div>

              <div className="space-y-2 group">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="password"
                    className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground group-focus-within:text-foreground transition-colors"
                  >
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="h-10 sm:h-12 bg-transparent border-x-0 border-t-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary transition-all pr-10 text-sm sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-2"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full text-base font-medium mt-6 sm:mt-8"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs sm:text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-medium text-foreground hover:underline underline-offset-4 transition-all"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right Pane - Visual */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-muted">
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent z-10" />
        <img
          src="/cdn-cgi/image/f=webp,w=1024/landing/watch.png"
          alt="Luxury watch product photography"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-10000 hover:scale-105 ease-out"
          decoding="async"
          fetchpriority="low"
        />
        <div
          className="absolute bottom-12 left-12 right-12 z-20 animate-fade-in"
          style={{ animationDelay: "300ms", animationFillMode: "both" }}
        >
          <p className="text-2xl font-serif text-white/90 leading-snug">
            Pick up right where you left off. Your images and edits are always
            saved.
          </p>
        </div>
      </div>
    </div>
  );
}
